import { Component, Input, OnDestroy, OnInit } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { map } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { AppState, vehiclesStateTableConfig } from "src/app/store/reducers";
import {
  getVehicleExcelList,
  getVehicleList,
  resetVehiclesState,
} from "src/app/store/actions/vehicles/vehicles.actions";

import {
  ColumnSorterInfo,
  DefaultPage,
  ExportEvent,
  FilterEvent,
  FilterName,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";

import {
  removeColumnSorterInfoOptions,
  removeTableConfigOptions,
  setAdvancedFilter,
  setColumnInfoCategoriesProperties,
} from "src/app/shared/utils/tableUtils";

import { BaseVehiclesList } from "src/app/shared/base-classes/base-vehicles-list/base-vehicles-list";

const USAGE_CODE = "CUSTOMER.VEHICLE";

@Component({
  selector: "customer-vehicles-list",
  templateUrl: "./customer-vehicles-list.component.html",
  styleUrls: ["./customer-vehicles-list.component.scss"],
})
export class CustomerVehiclesListComponent
  extends BaseVehiclesList
  implements OnInit, OnDestroy
{
  @Input() set code(code: string) {
    if (code) {
      this._code = code;
      this.getVehicleCategoriesProperties(
        USAGE_CODE,
        this.getVehicles.bind(this)
      );
    }
  }

  _code: string;

  constructor(store: Store<AppState>, translate: TranslateService) {
    super(store, translate);

    this.columnInfo$ = this.store.pipe(select(vehiclesStateTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          columnInfo = removeColumnSorterInfoOptions(columnInfo);
          columnInfo = setColumnInfoCategoriesProperties(
            columnInfo,
            this.tableConfig
          );
          return columnInfo;
        }
      })
    );
  }

  ngOnInit(): void {
    this.setTranslatedTitle();
    this.tableConfig = removeTableConfigOptions(this.tableConfig);
  }

  setTranslatedTitle() {
    this.translate
      .get("Tabs.Vehicles")
      .subscribe((translatedTitle) => (this.tableTitle = translatedTitle));
  }

  getVehicles(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.store.dispatch(
      getVehicleList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: advancedFilter,
        },
      })
    );
  }

  getVehicleExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.store.dispatch(
      getVehicleExcelList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: advancedFilter,
        },
      })
    );
  }

  setAdvancedFilter(filterEvent: FilterEvent) {
    const customerVehiclesFilter = {
      column: "customer.code",
      operator: FilterName.EQUALS_TO,
      value: this._code,
    };

    return setAdvancedFilter(customerVehiclesFilter, filterEvent);
  }

  onGetTableData(tableEvent: TableEvent) {
    this.getTableData(tableEvent, this.getVehicles.bind(this));
  }

  onGetExportData(exportEvent: ExportEvent) {
    this.getExportData(exportEvent, this.getVehicleExcelList.bind(this));
  }

  onSaveTableConfig(columnInfo: ColumnSorterInfo[]) {
    this.saveTableConfig(columnInfo, USAGE_CODE);
  }

  ngOnDestroy() {
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    this.store.dispatch(resetVehiclesState());
  }
}
